import { create } from 'zustand';
import axios from 'axios';
import { Platform } from 'react-native';
import { authStorage, STORAGE_KEYS } from './storage';
import { queryClient } from '../context/QueryClient';
import { authLogin, authRefreshToken } from '../api/endpoints/auth/auth';
import { getUser } from '../api/endpoints/users/users';
import log from '../utils/logger';
import { recordServerTimeSample } from '../time/serverNow';

import type { LoginRequest, User } from '../api/models';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      requestStartedAtMs?: number;
    };
  }
}

const SERVER_TIME_HEADER = 'x-server-time';

function readResponseHeader(headers: unknown, name: string): string | undefined {
  if (headers == null || typeof headers !== 'object') return undefined;
  const maybeGet = headers as { get?: (key: string) => unknown };
  if (typeof maybeGet.get === 'function') {
    const value = maybeGet.get(name);
    return value == null ? undefined : String(value);
  }
  const record = headers as Record<string, unknown>;
  const raw = record[name.toLowerCase()] ?? record[name];
  if (Array.isArray(raw)) return raw[0] == null ? undefined : String(raw[0]);
  return raw == null ? undefined : String(raw);
}

function recordServerTimeFromResponse(response: { headers: unknown; config: { metadata?: { requestStartedAtMs?: number } } }): void {
  const startedAt = response.config.metadata?.requestStartedAtMs;
  if (startedAt == null) return;
  const raw = readResponseHeader(response.headers, SERVER_TIME_HEADER);
  const serverTimeMs = raw == null ? Number.NaN : Number(raw);
  if (!Number.isFinite(serverTimeMs)) return;
  recordServerTimeSample(serverTimeMs, startedAt, Date.now());
}

// Configure axios defaults as early as possible at module level
// to avoid race conditions during app bootstrapping.
// In tests, environment.js pre-sets axios.defaults.baseURL, so only override if we have a value
const API_URL = (process.env.EXPO_PUBLIC_API_URL || '').trim();
if (API_URL) {
  axios.defaults.baseURL = API_URL;
} else if (!axios.defaults.baseURL) {
  // No env var and no existing baseURL - log error in production
  if (!__DEV__) {
    log.error('[CRITICAL] EXPO_PUBLIC_API_URL is not defined or empty in environment variables!');
  }
}

const REFRESH_MARGIN = 60 * 1000; // 1 minute before expiry
const MIN_REFRESH_INTERVAL = 10000; // 10 seconds minimum between refresh attempts
const MAX_REFRESH_EXPIRY_SECONDS = 60 * 60 * 24 * 30; // 30 days safety cap

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  impersonatedBy: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  isPersistent: boolean;
  refreshTimeout: NodeJS.Timeout | null;
  lastRefreshAttempt: number | null;
  pendingDeepLinkUrl: string | null;

  // Methods
  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateTokens: (tokens: AuthTokens) => Promise<void>;
  setPersistence: (isPersistent: boolean) => Promise<void>;
  setPendingDeepLinkUrl: (url: string | null) => void;
  isFeatureActive: (flagName: string) => boolean;
}

interface TokenPayload {
  exp?: number;
  impersonatedBy?: string;
  [key: string]: unknown;
}

function normalizeExpiresInSeconds(expiresIn?: number): number | null {
  if (typeof expiresIn !== 'number' || !Number.isFinite(expiresIn)) return null;
  if (expiresIn <= 0) return null;
  if (expiresIn > MAX_REFRESH_EXPIRY_SECONDS) return MAX_REFRESH_EXPIRY_SECONDS;
  return Math.floor(expiresIn);
}

// Helper to decode JWT and get payload
function getTokenPayload(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

// Helper to decode JWT and get expiration
function getTokenExpiration(token: string): number | null {
  const payload = getTokenPayload(token);
  return payload?.exp ? payload.exp * 1000 : null; // Convert to milliseconds
}

// Helper to check if token needs refresh
function shouldRefreshToken(token: string): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true; // If we can't decode, better refresh to be safe

  const now = Date.now();
  const timeUntilExpiry = expiration - now;

  // Refresh if token expires in less than REFRESH_MARGIN
  return timeUntilExpiry < REFRESH_MARGIN;
}

// Setup axios interceptors for token refresh
function setupAuthInterceptors() {
  // Create a separate client for refresh requests to avoid interceptor loop
  const refreshClient = axios.create({
    baseURL: process.env.EXPO_PUBLIC_API_URL,
    timeout: 10000,
  });

  axios.interceptors.request.use((config) => ({
    ...config,
    metadata: {
      ...config.metadata,
      requestStartedAtMs: Date.now(),
    },
  }));

  axios.interceptors.response.use(
    (response) => {
      recordServerTimeFromResponse(response);
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (
        !originalRequest
        // eslint-disable-next-line no-underscore-dangle
        || originalRequest._retry
        || error.response?.status !== 401
        || !['AUTH_TOKEN_EXPIRED', 'AUTH_INVALID_TOKEN'].includes(error.response?.data?.error?.code)
        || originalRequest.url?.includes('/auth/refresh')
      ) {
        return Promise.reject(error);
      }

      // console.log("Interceptor: api call failed with 401, refreshing token");

      try {
        // Always get fresh state using the hook's method
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const currentState = useAuthStore.getState();

        // Check if we've attempted a refresh too recently
        const now = Date.now();
        if (currentState.lastRefreshAttempt && now - currentState.lastRefreshAttempt < MIN_REFRESH_INTERVAL) {
          return await Promise.reject(error);
        }

        // Get the latest refreshToken from the store
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const { refreshToken } = useAuthStore.getState();
        // Expected edge: access token expired/invalid but refresh token already cleared
        // (logout, expired password-reset session, storage wipe). Logout quietly —
        // do not throw a synthetic Error (Sentry FLARE-4N noise via log.error/throw).
        if (!refreshToken) {
          log.warn('No refresh token available in store state; logging out');
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          await useAuthStore.getState().logout();
          return await Promise.reject(error);
        }

        // Direct refresh call without using a shared promise
        const response = await refreshClient.post('/auth/refresh', { refreshToken });
        const tokenData = response.data.data;

        // Update tokens in the store
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        await useAuthStore.getState().updateTokens(tokenData);
        const newAccessToken = tokenData.accessToken;

        // Update the original request's authorization header and mark it as a retry
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true;
        return await axios(originalRequest);
      } catch (refreshError) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        await useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    },
  );
}

export const useAuthStore = create<AuthState>((set, get) => {
  const store = {
    // State
    accessToken: null,
    refreshToken: null,
    user: null,
    impersonatedBy: null,
    isAuthenticated: false,
    isInitializing: true,
    isPersistent: false,
    refreshTimeout: null,
    lastRefreshAttempt: null,
    pendingDeepLinkUrl: null,

    // Initialize auth state
    initialize: async () => {
      try {
        // Setup interceptors (baseURL already set at module level)
        setupAuthInterceptors();

        // Apply forced tenant across all platforms if provided; otherwise apply mobile-only tenant
        {
          const forcedTenant = (process.env.EXPO_PUBLIC_FORCE_TENANT || '').trim();
          if (forcedTenant) {
            log.info('Setting forced tenant', forcedTenant);
            axios.defaults.headers.common['X-Tenant-Id'] = forcedTenant;
          } else if (Platform.OS !== 'web') {
            const tenantId = process.env.EXPO_PUBLIC_MOBILE_TENANT_ID;
            if (tenantId) {
              axios.defaults.headers.common['X-Tenant-Id'] = tenantId;
            }
          }
        }

        // Check for impersonation handoff (Web only)
        if (Platform.OS === 'web') {
          const handoffRaw = localStorage.getItem(STORAGE_KEYS.IMPERSONATION_HANDOFF);
          if (handoffRaw) {
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATION_HANDOFF);
            try {
              const handoff = JSON.parse(handoffRaw);
              // Only accept if it's fresh (within 30 seconds)
              if (handoff.accessToken && handoff.refreshToken && Date.now() - handoff.timestamp < 30000) {
                log.info('Detected impersonation handoff, setting tokens');
                // We set tokens directly in sessionStorage to avoid clearing localStorage in other tabs
                sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, handoff.accessToken);
                sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, handoff.refreshToken);
                await authStorage.setPersistence(false);
              }
            } catch (e) {
              log.error('Failed to parse impersonation handoff', e);
            }
          }
        }

        // Restore persistence setting
        const isPersistent = await authStorage.getPersistence();
        set({ isPersistent });

        // Try to restore tokens
        const tokens = await authStorage.getTokens(isPersistent);
        if (!tokens.accessToken || !tokens.refreshToken) {
          await store.logout();
          return;
        }

        // Set initial state with tokens
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        });

        // Set axios headers
        axios.defaults.headers.common.Authorization = `Bearer ${tokens.accessToken}`;

        try {
          // Check if we need to refresh the token
          if (shouldRefreshToken(tokens.accessToken)) {
            const refreshResponse = await authRefreshToken({ refreshToken: tokens.refreshToken });
            const tokenData = refreshResponse.data.data;

            if (!tokenData || typeof tokenData === 'string') {
              throw new Error('Invalid token format received from refresh response');
            }

            await store.updateTokens(tokenData);
          } else {
            // Even if we don't need an immediate refresh, schedule the next one
            const expiration = getTokenExpiration(tokens.accessToken);
            if (expiration) {
              const now = Date.now();
              const expiresIn = Math.floor((expiration - now) / 1000); // Convert to seconds
              await store.updateTokens({
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                expiresIn,
              });
            }
          }

          // Get user data
          const userResponse = await getUser('me');
          const userData = userResponse.data.data;

          set({
            user: userData,
          });
        } catch (error) {
          await store.logout();
        }

        // Ensure tenant header is set; forced tenant takes precedence, then mobile-only
        {
          const forcedTenant = (process.env.EXPO_PUBLIC_FORCE_TENANT || '').trim();
          if (forcedTenant) {
            axios.defaults.headers.common['X-Tenant-Id'] = forcedTenant;
          } else if (Platform.OS !== 'web') {
            const tenantId = process.env.EXPO_PUBLIC_MOBILE_TENANT_ID;
            if (tenantId) {
              axios.defaults.headers.common['X-Tenant-Id'] = tenantId;
            }
          }
        }
      } catch (error) {
        await store.logout();
      } finally {
        set({ isInitializing: false });
      }
    },

    // Token management
    updateTokens: async ({ accessToken, refreshToken, expiresIn }) => {
      set({ lastRefreshAttempt: Date.now() });

      // Decode payload to check for impersonation
      const payload = getTokenPayload(accessToken);
      const impersonatedBy = payload?.impersonatedBy || null;

      // Update storage first
      await authStorage.setTokens({ accessToken, refreshToken, expiresIn }, get().isPersistent);

      // Update axios headers
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      // Clear existing refresh timeout if any
      const currentTimeout = get().refreshTimeout;
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }

      // Get current state to preserve user-related fields
      const currentState = get();

      // Prepare new state updates while preserving user-related state
      const updates: Partial<AuthState> = {
        accessToken,
        refreshToken,
        impersonatedBy,
        refreshTimeout: null,
        isAuthenticated: true,
        // Preserve user state
        user: currentState.user,
      };

      // Schedule new refresh only for sane expiry values.
      const normalizedExpiresIn = normalizeExpiresInSeconds(expiresIn);
      if (normalizedExpiresIn && normalizedExpiresIn * 1000 > REFRESH_MARGIN + MIN_REFRESH_INTERVAL) {
        const refreshTime = normalizedExpiresIn * 1000 - REFRESH_MARGIN;

        updates.refreshTimeout = setTimeout(async () => {
          try {
            const response = await authRefreshToken({ refreshToken });
            const tokenData = response.data.data;

            if (!tokenData || typeof tokenData === 'string') {
              throw new Error('Invalid token format received from refresh response');
            }

            // Use the fresh store reference instead of the stale 'store'
            await useAuthStore.getState().updateTokens(tokenData);
          } catch (error) {
            // Use the fresh store reference for logout as well
            await useAuthStore.getState().logout();
          }
        }, refreshTime);
      }

      // Update all state at once
      set(updates);
    },

    // Auth actions
    login: async (credentials: LoginRequest) => {
      // Update persistence first to avoid race condition
      await get().setPersistence(credentials.rememberMe ?? false);

      const response = await authLogin(credentials);
      const loginData = response.data.data;

      if (!loginData) {
        // Wrap the response into an error so that error.response is available
        const err = new Error(response.data.message || 'Login failed: no data received') as Error & {
          response?: { status: number; data: unknown };
        };
        err.response = {
          status: response.data.statusCode,
          data: response.data,
        };
        throw err;
      }

      if (typeof loginData.token === 'string') {
        throw new Error('Invalid token format received from login response');
      }

      // Update tokens first (handles storage and headers)
      await store.updateTokens(loginData.token);

      // Then update user state
      set({
        user: loginData.user,
        isAuthenticated: true,
        pendingDeepLinkUrl: null,
      });
    },

    logout: async () => {
      const state = get();

      if (state.refreshTimeout) {
        clearTimeout(state.refreshTimeout);
      }

      await authStorage.clearTokens();
      await queryClient.clear();

      delete axios.defaults.headers.common.Authorization;

      set({
        accessToken: null,
        refreshToken: null,
        user: null,
        impersonatedBy: null,
        isAuthenticated: false,
        refreshTimeout: null,
        pendingDeepLinkUrl: null,
      });
    },

    setPersistence: async (isPersistent) => {
      await authStorage.setPersistence(isPersistent);

      const state = get();
      if (state.accessToken || state.refreshToken) {
        await authStorage.setTokens(
          {
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
          },
          isPersistent,
        );
      }

      set({ isPersistent });
    },

    setPendingDeepLinkUrl: (url: string | null) => {
      set({ pendingDeepLinkUrl: url });
    },

    isFeatureActive: (flagName: string) => {
      const state = get();
      return !!state.user?.features?.[flagName];
    },
  };

  // For backwards compatibility
  Object.defineProperty(get, 'apiClient', {
    get: () => axios,
  });

  return store;
});
